import { supabase } from "@/integrations/supabase/client";

/**
 * Generate a short-lived signed URL for a private storage object.
 * Falls back to null if the path is missing or the request fails.
 *
 * @param bucket   Storage bucket name (e.g. "documents", "journey-documents")
 * @param filePath Path inside the bucket (e.g. "generated/my-doc-uuid.pdf")
 * @param expiresIn Lifetime in seconds (default 3600 = 1 hour)
 */
export async function getSignedUrl(
  bucket: string,
  filePath: string,
  expiresIn = 3600,
): Promise<string | null> {
  if (!filePath) return null;

  if (/^https?:\/\//i.test(filePath)) {
    return filePath;
  }

  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(filePath, expiresIn);

  if (error) {
    console.error("Failed to create signed URL:", error);
    return null;
  }

  return data.signedUrl;
}

/**
 * Determine the bucket name from a file_path or file_url.
 * Defaults to "documents".
 */
export function getBucketFromPath(filePath?: string | null): string {
  if (!filePath) return "documents";
  if (filePath.startsWith("journey-documents/") || filePath.includes("/journey-documents/")) {
    return "journey-documents";
  }
  return "documents";
}

/**
 * Given a document record that may have file_path and/or file_url,
 * return a signed URL for viewing / downloading. Prefers file_path.
 */
export async function getDocumentSignedUrl(
  doc: { file_path?: string | null; file_url?: string | null; signed_file_url?: string | null; original_file_url?: string | null },
  expiresIn = 3600,
): Promise<string | null> {
  const filePath = doc.file_path || doc.signed_file_url || doc.original_file_url || doc.file_url;

  if (filePath) {
    const bucket = getBucketFromPath(filePath);
    return getSignedUrl(bucket, filePath, expiresIn);
  }

  return null;
}
